import { ColDef } from 'ag-grid-community';
import { CellTypes, getInventoryFilterValues } from '../../services/inventory';
import { Inventory } from '../../types/inventory';

interface InventoryColDef<T> extends ColDef<T> {
  type: keyof typeof CellTypes;
  field: any;
  hide?: boolean;
  suppressFiltersToolPanel?: boolean;
  suppressColumnsToolPanel?: boolean;
}

const toolTipValueGetter = (params: any) => {
  return 'Copy';
};

const formatDateValue = (dateValue: string) => {
  if (dateValue && dateValue.length > 0) {
    return dateValue;
  }
  return '-';
  return '';
};

const formatCPOValue = (isCertifiedPreOwned: any) => {
  if (
    (typeof isCertifiedPreOwned === 'boolean' && isCertifiedPreOwned) ||
    (typeof isCertifiedPreOwned === 'string' && isCertifiedPreOwned === 'CPO')
  ) {
    return 'CPO';
  }
  if (typeof isCertifiedPreOwned === 'boolean' && !isCertifiedPreOwned) {
    return 'Non-CPO';
  }
  return isCertifiedPreOwned;
};

const formaCpoWarrantyValue = (
  country: string,
  isCertifiedPreOwned: any,
  cpoWarrantyTermMileage: number,
  cpoWarrantyTermDuration: string
) => {
  let mileageUnit = 'MI';
  if (country && country.length > 0 && country !== 'United States') {
    mileageUnit = 'KM';
  }
  const CpoQualified =
    (typeof isCertifiedPreOwned === 'boolean' && isCertifiedPreOwned) ||
    (typeof isCertifiedPreOwned === 'string' && isCertifiedPreOwned === 'CPO');

  if (CpoQualified && cpoWarrantyTermMileage && cpoWarrantyTermDuration) {
    return `${cpoWarrantyTermMileage} ${mileageUnit} / ${cpoWarrantyTermDuration}`;
  }
  if (CpoQualified && cpoWarrantyTermMileage && !cpoWarrantyTermDuration) {
    return `${cpoWarrantyTermMileage} ${mileageUnit}`;
  }
  if (CpoQualified && !cpoWarrantyTermMileage && cpoWarrantyTermDuration) {
    return `${cpoWarrantyTermDuration}`;
  }
  return '';
};

const formatOdometerValue = (country: string, odometer: string) => {
  if (country && country.length > 0) {
    if (country !== 'United States') {
      return `${odometer} KM`;
    }
  }
  return `${odometer} MI`;
};

const numberRangeFilterParams = {
  filterOptions: ['inRange'],
  maxNumConditions: 1,
};

const getFilterValuesAsync = async (params: any) => {
  const col = params.colDef.field;
  const data = await getInventoryFilterValues(col, '');
  return params.success(data.data);
};

const dateFilterParams: any = {
  browserDatePicker: true,
  minValidDate: '2020-01-01',
  maxValidDate: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    const dateAsString = new Date(cellValue);

    if (dateAsString == null) {
      return 0;
    }

    // Now that both parameters are Date objects, we can compare
    if (dateAsString < new Date(filterLocalDateAtMidnight)) {
      return -1;
    }
    if (dateAsString > new Date(filterLocalDateAtMidnight)) {
      return 1;
    }
    return 0;
  },
  // filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
  filterOptions: ['inRange'],
  maxNumConditions: 1,
};

const columnDefs: InventoryColDef<Inventory>[] = [
  {
    headerName: 'Batch Number',
    field: 'batchNumber',
    sortable: true,
    width: 180,
    filter: 'agNumberColumnFilter',
    type: 'Plain',
    hide: false,
    enableRowGroup: false,
  },
  {
    headerName: 'VIN',
    field: 'vin',
    sortable: true,
    width: 180,
    filter: 'agTextColumnFilter',
    type: 'Plain',
    hide: false,
    enableRowGroup: false,
  },
  {
    headerName: 'AMP1 Planned Broadcast Gate Date', // Name is intentionally changed as per business req
    field: 'actualBroadcastDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueGetter: (params: any) => formatDateValue(params?.data?.actualBroadcastDate),
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'AMP1 Factory Gate Date ',
    field: 'actualFactoryGateDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueGetter: (params: any) => formatDateValue(params?.data?.actualFactoryGateDate),
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'EU Port Date',
    field: 'euPortDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'AMP2 Actual Factory Gate Date',
    field: 'amp2ActualFactoryGateDate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Year',
    field: 'year',
    sortable: true,
    width: 100,
    filter: 'agTextColumnFilter',
    type: 'Plain',
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Model',
    field: 'carModel',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Variant',
    field: 'variant',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Exterior color',
    field: 'exteriorColor',
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Interior color',
    field: 'interiorColor',
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: false,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: getFilterValuesAsync,
      caseSensitive: true,
    },
  },
  {
    headerName: 'Appearance',
    field: 'appearance',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Roof',
    field: 'roof',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Drivetrain',
    field: 'driveTrain',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Wheels',
    field: 'wheels',
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'ADAS',
    field: 'ada',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Sound System',
    field: 'soundSystem',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Front Seats',
    field: 'lucidFrontSeating',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Rear Seats',
    field: 'lucidRearSeating',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Comfort & Convenience Package',
    field: 'lucidComfortAndConvenience',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Package',
    field: 'package',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Dynamic Handling Package',
    field: 'dynamicHandlingPackage',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Technology Package',
    field: 'technologyPackage',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Towing Package',
    field: 'towingPackage',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Suspension Damper',
    field: 'suspensionDamp',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Battery Pack',
    field: 'batteryPack',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'Bundle',
    field: 'bundleName',
    sortable: true,
    type: 'Bundle',
    width: 220,
    hide: false,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Price',
    field: 'totalPrice',
    filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    sortable: true,
    type: 'CurrencyDropDown',
    width: 220,
    hide: false,
  },
  {
    headerName: 'Studio Select Discount',
    field: 'studioCredit',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Location Name',
    field: 'locationName',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  // {
  //   headerName: 'Transportation Confirmed',
  //   field: 'transportationConfirmed',
  //   sortable: true,
  //   type: 'Plain',
  //   width: 180,
  //   hide: false,
  //   enableRowGroup: true,
  //   filter: 'agSetColumnFilter',
  // },
  {
    headerName: 'In Transit To Location',
    field: 'inTransitToLocation',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Current Country',
    field: 'transitCountry',
    sortable: true,
    type: 'Country',
    hide: false,
    width: 160,
  },
  {
    headerName: 'Current City',
    field: 'currentCity',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Current State',
    field: 'currentState',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Factory Campaigns',
    field: 'factoryCampaignCount',
    sortable: true,
    type: 'Plain',
    width: 180,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Listing Country',
    field: 'listingCountry',
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 160,
  },
  {
    headerName: 'Agent Listing Country',
    field: 'agentListingCountry',
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 180,
  },
  {
    headerName: 'Importer Listing Country',
    field: 'importerListingCountry',
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 200,
  },
  {
    headerName: 'Homologation Country',
    field: 'country',
    sortable: true,
    type: 'Plain',
    width: 200,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Title Status',
    field: 'titleStatus',
    sortable: true,
    type: 'Plain',
    width: 140,
    hide: false,
    enableRowGroup: true,
  },
  {
    headerName: 'Sales Tax',
    field: 'salesTax',
    sortable: true,
    type: 'Plain',
    width: 140,
    minWidth: 140,
    suppressSizeToFit: true,
    hide: false,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: getFilterValuesAsync,
      caseSensitive: true,
    },
  },
  {
    headerName: 'Sales Channel',
    field: 'salesChannel',
    sortable: true,
    type: 'Plain',
    width: 150,
    minWidth: 150,
    suppressSizeToFit: true,
    hide: false,
    enableRowGroup: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: getFilterValuesAsync,
      caseSensitive: true,
    },
  },
  {
    headerName: 'Certified PreOwned',
    field: 'isCertifiedPreOwned',
    valueGetter: (params: any) => formatCPOValue(params?.data?.isCertifiedPreOwned),
    sortable: true,
    type: 'Plain',
    width: 150,
    hide: true,
    enableRowGroup: true,
  },
  {
    headerName: 'CPO Program',
    field: 'cpoWarrantyTermMileage',
    // filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    valueGetter: (params: any) =>
      formaCpoWarrantyValue(
        params?.data?.country,
        params?.data?.isCertifiedPreOwned,
        params?.data?.cpoWarrantyTermMileage,
        params?.data?.cpoWarrantyTermDuration
      ),
    sortable: true,
    type: 'Plain',
    hide: true,
    width: 220,
    suppressFiltersToolPanel: true,
    filter: false,
  },
  {
    headerName: 'Odometer',
    field: 'odometer',
    filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    valueGetter: (params: any) => formatOdometerValue(params?.data?.country, params?.data?.odometer),
    sortable: true,
    type: 'Plain',
    hide: false,
    width: 220,
    minWidth: 220,
    suppressSizeToFit: true,
  },
  {
    headerName: 'State of Charge',
    field: 'stateOfCharge',
    filter: 'agNumberColumnFilter',
    filterParams: numberRangeFilterParams,
    sortable: true,
    type: 'Plain',
    hide: true,
    width: 220,
  },
  {
    headerName: 'OTA Status',
    field: 'otaStatus',
    sortable: true,
    type: 'Plain',
    hide: true,
    width: 220,
  },
  {
    headerName: 'OTA Version',
    field: 'otaVersion',
    sortable: true,
    type: 'Plain',
    hide: true,
    width: 220,
  },
  {
    headerName: 'Last Telemetry Update',
    field: 'lastTelemeteryUpdate',
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    valueGetter: (params: any) => formatDateValue(params?.data?.lastTelemeteryUpdate),
    sortable: true,
    type: 'Plain',
    hide: true,
    width: 220,
  },
  {
    headerName: 'Availability',
    field: 'availability',
    sortable: false,
    type: 'Plain',
    width: 180,
    hide: false,
    filter: false,
    floatingFilter: false,
    suppressFiltersToolPanel: true,
    enableRowGroup: false,
  },
  {
    headerName: 'Link',
    field: 'weblink',
    sortable: false,
    type: 'Plain',
    width: 90,
    pinned: 'right',
    enableRowGroup: false,
    suppressFiltersToolPanel: true,
  },
  {
    headerName: 'Status',
    field: 'subCategory',
    sortable: true,
    type: 'Tag',
    width: 120,
    pinned: 'right',
    enableRowGroup: true,
  },
];

export const metaData = {
  _id: '6316fd3926673f830a4d64f7',
  module: 'Vehicles',
  subModule: 'Grid',
  columnDefs,
  filterDetails: [
    'Year',
    'Model',
    'Trim',
    'Variant',
    'Vehicle Stage',
    'Vehicle Substage',
    'Interior color',
    'Exterior color',
    'Wheels',
    'Battery pack',
    'Roof',
    'Drivetrain',
    'Price',
    'Suspension Damper',
    'ADAS',
    'Look',
    'Sound System',
    'Current City',
    'Current State',
    'Seats',
    'Comfort & Convenience Package',
    'Package',
    'Status',
    'Title Status',
    'Sales Tax',
    'Sales Channel',
    'Transportation Confirmed',
  ],
  manageColumns: [
    'Year',
    'Model',
    'Trim',
    'Variant',
    'Vehicle Stage',
    'Vehicle Substage',
    'Interior color',
    'Exterior color',
    'Wheels',
    'Battery pack',
    'Roof',
    'Drivetrain',
    'Price',
    'Suspension Damper',
    'ADAS',
    'Look',
    'Sound System',
    'Current City',
    'Current State',
    'Seats',
    'Comfort & Convenience Package',
    'Package',
    'Title Status',
    'Sales Tax',
    'Sales Channel',
    'Status',
    'Transportation Confirmed',
  ],
  langCode: 'en-us',
};
